import { createReducer } from '@reduxjs/toolkit'

import type { TSimChatDto, TSimChatSummaryDto } from '@homepal/api-contract'

import * as actions from 'redux/actions/internal/SimChats/actions'
import * as authenticationActions from 'redux/actions/Authentication/actions'

export interface SimChatReducerType {
  /** The chats matching the latest filter, newest activity first. */
  list: TSimChatSummaryDto[]
  /** Chats matching the filter, beyond the page shown. */
  totalCount: number
  listFetched: boolean
  listFetching: boolean
  /** Filter of the latest list request, so an older response is dropped. */
  requestedListKey: string | null
  /** Full chats by id, kept across list refreshes. */
  chats: Record<string, TSimChatDto>
  /** Ids of the chats being fetched. */
  fetchingChatIds: string[]
}

const INITIAL_STATE: SimChatReducerType = {
  list: [],
  totalCount: 0,
  listFetched: false,
  listFetching: false,
  requestedListKey: null,
  chats: {},
  fetchingChatIds: []
}

// Reducer (createReducer uses immer — cases mutate the draft directly)
export default createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(actions.getAll, (state, action) => {
      state.requestedListKey = action.payload.requestKey
      state.listFetching = true
    })
    .addCase(actions.getAllSuccess, (state, action) => {
      const { payload } = action

      // Changing a filter fires a new request while the previous one is
      // still in flight, and the two can come back out of order.
      if (payload.requestKey !== state.requestedListKey) {
        return
      }

      state.list = payload.data.chats
      state.totalCount = payload.data.total_count
      state.listFetched = true
      state.listFetching = false
    })
    .addCase(actions.getAllFailed, (state, action) => {
      if (action.payload.requestKey !== state.requestedListKey) {
        return
      }

      state.listFetching = false
    })
    .addCase(actions.getOne, (state, action) => {
      if (!state.fetchingChatIds.includes(action.payload.id)) {
        state.fetchingChatIds.push(action.payload.id)
      }
    })
    .addCase(actions.getOneSuccess, (state, action) => {
      state.chats[action.payload.id] = action.payload
      state.fetchingChatIds = state.fetchingChatIds.filter(
        (id) => id !== action.payload.id
      )
    })
    .addCase(actions.getOneFailed, (state, action) => {
      state.fetchingChatIds = state.fetchingChatIds.filter(
        (id) => id !== action.payload.id
      )
    })
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
})
